import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import axiosInstance from "../util/axiosInstance";
import { useLoading } from "../components/LoadingContext";
import { checkSession } from "../util/checkSession";

// 👇 Import context
import { useSurveyContext } from "../context/SurveyContext";
import { API_KEY } from "../config";

interface IWorkArrangementsTool {
  partitionKey: string;
  rowKey: string;
  riskFactor: string;
  firstOption: string;
  secondOption: string;
  thirdOption: string;
  fourthOption: string;
  fifthOption: string;
}

const Container = styled.div`
  background-color: #000;
  color: white;
  padding: 20px;
  min-height: 78.5vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.img`
  width: 120px;
  margin-bottom: 10px;
`;

const Title = styled.h2`
  color: #FF372F;
  margin: 10px 0;
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  max-width: 600px;
  margin-top: 20px;
`;

const FieldSet = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  color: #FF372F;
  font-weight: bold;
  display: block;
  margin-bottom: 5px;
`;

const QuestionText = styled.p`
  margin-bottom: 10px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 4px;
  border: 2px solid #CCC;
  appearance: none;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
`;

const SelectWrapper = styled.div`
  position: relative;

  &::after {
    content: "▼";
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    color: #888;
    pointer-events: none;
  }
`;

const Button = styled.button`
  background-color: #FF372F;
  color: white;
  padding: 12px 20px;
  font-size: 16px;
  border: none;
  border-radius: 3px;
  margin-top: 30px;
  width: 100%;
  cursor: pointer;

  &:disabled {
    background-color: #888;
    cursor: not-allowed;
  }
`;

const WorkArrangementsTool: React.FC = () => {
  const { setLoading } = useLoading();
  const { surveyData, setSurveyData } = useSurveyContext(); // 👈 Add context
  const [questions, setQuestions] = useState<IWorkArrangementsTool[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(`/GetMasterData/WorkArrangementsTool?code=${API_KEY}`)
      .then((response) => {
        setQuestions(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [setLoading]);

  const handleChange = (rowKey: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [rowKey]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    checkSession();

    // 👉 Store WorkArrangements answers in context
    setSurveyData({
      ...surveyData,
      WorkArrangementsTool: answers,
    });

    navigate("/PriorSleepWakeTool");
  };

  const isFormComplete = questions.every((q) => answers[q.rowKey]);
  const photo = questions[0]?.partitionKey?.split(":")[0] || "";

  return (
    <Container>
      <Logo src={`${photo}.png`} alt={`${photo} Logo`} />
      <Title>{questions[0]?.partitionKey}</Title>
      <Form onSubmit={handleSubmit}>
        {questions.map((q) => {
          const options = [
            q.firstOption,
            q.secondOption,
            q.thirdOption,
            q.fourthOption,
            q.fifthOption,
          ].filter(Boolean);

          return (
            <FieldSet key={q.rowKey}>
              <QuestionText>{q.rowKey} - {q.riskFactor}</QuestionText>
              <SelectWrapper>
                <Select
                  value={answers[q.rowKey] || ""}
                  onChange={(e) => handleChange(q.rowKey, e.target.value)}
                >
                  <option value="">Select an option</option>
                  {options.map((opt, idx) => (
                    <option key={idx} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </SelectWrapper>
            </FieldSet>
          );
        })}

        <Button type="submit" disabled={!isFormComplete}>
          Continue
        </Button>
      </Form>
    </Container>
  );
};

export default WorkArrangementsTool;
